import React, { useState, useEffect, useRef } from 'react';
import { sortBy, uniq } from 'underscore';
import Select from 'react-select';
import { DEFAULT_OPTIONS_PAYLOAD } from '../../../common/Table';
import { useGetCompanies, useGetCompanyLocations, useGetDepartmentUserMappings } from '../../../../backend/masters';
import { useGetUserCompanies as useGetUserCompaniesTree } from '../../../../backend/query';
import { FULL_USER_COMPANIES_PAYLOAD } from '../../../../backend/common/noticeManagementUtils';
import { getUserDetails, hasUserAccess } from '../../../../backend/auth';
import { ComplianceDashboardPayload } from '../../../../backend/complianceDashboardV2';
import { USER_PRIVILEGES } from '../../UserManagement/Roles/RoleConfiguration';
// ── Time Range helpers ─────────────────────────────────────────────────────────

interface TimeRangeOption {
    value: string;
    label: string;
    financial_year: string;
    quarter: string;
}

function getCurrentIndianFY(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const start = month >= 4 ? year : year - 1;
    return `${start}-${String(start + 1).slice(-2)}`;
}

function generateTimeRangeOptions(): TimeRangeOption[] {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const currentFYStart = month >= 4 ? year : year - 1;
    const currentFY = `${currentFYStart}-${String(currentFYStart + 1).slice(-2)}`;

    const opts: TimeRangeOption[] = [
        { value: 'full', label: `Current Year (${currentFYStart})`, financial_year: currentFY, quarter: '' },
        { value: 'q1',   label: `Q1 ${currentFYStart}`,             financial_year: currentFY, quarter: 'q1' },
        { value: 'q2',   label: `Q2 ${currentFYStart}`,             financial_year: currentFY, quarter: 'q2' },
        { value: 'q3',   label: `Q3 ${currentFYStart}`,             financial_year: currentFY, quarter: 'q3' },
        { value: 'q4',   label: `Q4 ${currentFYStart + 1}`,         financial_year: currentFY, quarter: 'q4' },
    ];

    // Current FY as an explicit selectable entry, then past FYs
    opts.push({ value: `fy_${currentFY}`, label: `FY ${currentFY}`, financial_year: currentFY, quarter: '' });
    for (let i = 1; i <= 5; i++) {
        const start = currentFYStart - i;
        const fy = `${start}-${String(start + 1).slice(-2)}`;
        opts.push({ value: `fy_${fy}`, label: `FY ${fy}`, financial_year: fy, quarter: '' });
    }

    return opts;
}

const TIME_RANGE_OPTIONS = generateTimeRangeOptions();
const DEFAULT_TIME_RANGE = TIME_RANGE_OPTIONS[0]; // 'Full Year'

const DEFAULT_VALUE = 'ALL';
const DEFAULT_OPTION = { value: DEFAULT_VALUE, label: 'All' };

// ── react-select styles ────────────────────────────────────────────────────────

const selectStyles = {
    control: (base: any) => ({
        ...base,
        minHeight: 38,
        height: 38,
        fontSize: 14,
        borderColor: '#e6e6e6',
        borderRadius: 8,
        boxShadow: 'none',
        '&:hover': { borderColor: '#2965ad' },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '0 12px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any) => ({ ...base, padding: '0 8px' }),
    menu: (base: any) => ({ ...base, fontSize: 13, zIndex: 9999 }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected ? '#2965ad' : state.isFocused ? '#f0f5ff' : '#fff',
        color: state.isSelected ? '#fff' : '#1e1e1e',
    }),
    singleValue: (base: any) => ({ ...base, color: '#1e1e1e', fontFamily: 'Montserrat, sans-serif' }),
    placeholder: (base: any) => ({ ...base, color: '#757575' }),
};

// Shared date/month input style
const dateInputStyle: React.CSSProperties = {
    height: 38,
    width: '100%',
    border: '1px solid #e6e6e6',
    borderRadius: 8,
    padding: '0 12px',
    fontSize: 13,
    color: '#1e1e1e',
    fontFamily: 'Montserrat, sans-serif',
    background: '#fff',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
};

// ── Activity Tabs ──────────────────────────────────────────────────────────────

const ACTIVITY_TABS = [
    'All', 'Appointment', 'Assurance', 'Audit', 'Deposit',
    'Display', 'Internal Compliance', 'Intimation', 'Meeting', 'Policy',
    'Registers', 'Registration', 'Returns', 'Rule',
];

// Month options for the cross-browser Month & Year picker
const MONTH_OPTIONS = [
    { value: '01', label: 'January'   }, { value: '02', label: 'February'  },
    { value: '03', label: 'March'     }, { value: '04', label: 'April'     },
    { value: '05', label: 'May'       }, { value: '06', label: 'June'      },
    { value: '07', label: 'July'      }, { value: '08', label: 'August'    },
    { value: '09', label: 'September' }, { value: '10', label: 'October'   },
    { value: '11', label: 'November'  }, { value: '12', label: 'December'  },
];
function buildMonthYearOptions() {
    const cy = new Date().getFullYear();
    const years: number[] = [];
    for (let y = cy; y >= cy - 4; y--) years.push(y);
    return years;
}

// ── Penalty Metrics ────────────────────────────────────────────────────────────

export interface PenaltyMetrics {
    financial_exposure: { total_penalty_amount: number; avg_penalty_per_activity: number };
    imprisonment_risk:  { count: number; pct_of_non_compliant: number };
    license_revocation: { count: number; pct_of_non_compliant: number };
}

// ── Serialisable filter state (saved to sessionStorage before navigation) ──────

export interface FilterSaveState {
    filters: {
        company: any;
        associate_company: any;
        state: any;
        location: any;
    };
    timeRange: string;
    filterMonth: string;
    filterYear: string;
    monthYear: string;
    dueDate: string;
    selectedOwner: any;
    selectedManager: any;
    selectedVertical: any;
    selectedDepartment: any;
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
    onApply: (payload: ComplianceDashboardPayload) => void;
    selectedTab: string;
    onTabChange: (tab: string) => void;
    /** Saved filter-display state restored from sessionStorage after back-navigation */
    initialState?: FilterSaveState | null;
    /** Called whenever any filter value changes so the parent can track state for saving */
    onStateChange?: (state: FilterSaveState) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function ComplianceDashboardV2Filter({ onApply, selectedTab, onTabChange, initialState, onStateChange }: Props) {

    const { user_id: loginUser } = getUserDetails();
    const isOwner      = hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD);
    const isManager    = hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD);
    const isEscalation = hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD);

    // ── filter state ───────────────────────────────────────────────────────────
    const [filters, setFilters] = useState<any>({
        company: DEFAULT_OPTION,
        associate_company: null,
        state: null,
        location: null,
    });
    const filtersRef = useRef<any>();
    filtersRef.current = filters;

    // Set to true when state is restored from sessionStorage so that the
    // companies-loaded useEffect skips its default first-company auto-select.
    const restoredRef = useRef(false);

    // ── dropdown option lists ──────────────────────────────────────────────────
    const companiesLoadedRef = useRef(false);
    const [companies, setCompanies]                   = useState<any[]>([DEFAULT_OPTION]);
    const [associateCompanies, setAssociateCompanies] = useState<any[] | undefined>();
    const [allLocations, setAllLocations]             = useState<any[]>([]);
    const [locations, setLocations]                   = useState<any[] | undefined>();
    const [states, setStates]                         = useState<any[]>([]);
    const [owners,      setOwners]      = useState<any[]>([]);
    const [managers,    setManagers]    = useState<any[]>([]);
    const [verticals,   setVerticals]   = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);

    // ── time range ─────────────────────────────────────────────────────────────
    const [timeRange, setTimeRange] = useState<string>(DEFAULT_TIME_RANGE.value);

    // ── more filters state (Month & Year + Due Date + Owner + Manager) ─────────
    const [monthYear,        setMonthYear]        = useState<string>('');  // "YYYY-MM"
    // Separate parts for the two-select Month & Year picker.
    // Kept independent of monthYear so that selecting one half doesn't
    // require the other to already be set (chicken-and-egg problem with a single state).
    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear,  setFilterYear]  = useState('');
    const [dueDate,          setDueDate]          = useState<string>('');  // "YYYY-MM-DD"
    const [selectedOwner,      setSelectedOwner]      = useState<any>(null);
    const [selectedManager,    setSelectedManager]    = useState<any>(null);
    const [selectedVertical,   setSelectedVertical]   = useState<any>(null);
    const [selectedDepartment, setSelectedDepartment] = useState<any>(null);

    // ── more filters panel ─────────────────────────────────────────────────────
    const [showMore, setShowMore] = useState(false);
    const moreRef = useRef<HTMLDivElement>(null);

    // ── Restore filter state after back-navigation ────────────────────────────
    // Runs once on mount. If the parent provided a saved FilterSaveState (read
    // from sessionStorage), we restore every display field here. The parent
    // also calls setApiPayload(savedPayload) directly, so we do NOT call
    // onApply — that would reset selectedTab on the parent via handleFilterApply.
    useEffect(() => {
        if (!initialState) return;
        restoredRef.current = true;
        setFilters(initialState.filters);
        setTimeRange(initialState.timeRange);
        setFilterMonth(initialState.filterMonth);
        setFilterYear(initialState.filterYear);
        setMonthYear(initialState.monthYear);
        setDueDate(initialState.dueDate);
        setSelectedOwner(initialState.selectedOwner);
        setSelectedManager(initialState.selectedManager);
        setSelectedVertical(initialState.selectedVertical);
        setSelectedDepartment(initialState.selectedDepartment);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── API calls ─────────────────────────────────────────────────────────────

    // Escalation: single call returns the full company → AC → location tree.
    // Disabled for non-escalation roles (they use the dept-mapping cascade instead).
    const { userCompanies, isFetching: fetchingUserCompanies } =
        useGetUserCompaniesTree(FULL_USER_COMPANIES_PAYLOAD, isEscalation);

    // Dept mappings:
    //   • Escalation — scoped to selected company to derive owner/manager/vertical/dept lists.
    //     Disabled until a company is chosen.
    //   • Owner/Manager — full list to derive accessible companies + role-aware user lists.
    const escalationCompanyId = isEscalation ? ((filters.company || {}).value || null) : null;
    const { departmentUsers, isFetching: fetchingDeptUsers }: any = useGetDepartmentUserMappings(
        isEscalation
            ? { ...DEFAULT_OPTIONS_PAYLOAD, filters: { department_company_id_eq: escalationCompanyId, is_active_eq: true } }
            : { ...DEFAULT_OPTIONS_PAYLOAD, filters: { is_active_eq: true } },
        isEscalation ? (!!escalationCompanyId && escalationCompanyId !== DEFAULT_VALUE) : true,
    );

    // Non-escalation only: separate AC and location API calls (cascading).
    const { companies: acs, isFetching: fetchingAcs } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {
            is_parent_eq: 'false',
            parent_company_id_eq: (filters.company || {}).value,
            is_active_eq: true,
        },
        sorts: ['name asc'],
    }, !isEscalation && filters.company && filters.company.value !== DEFAULT_VALUE);

    const { locations: locs, isFetching: fetchingLocs } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: { company_id_eq: (filters.associate_company || {}).value },
        include: { location: ['state'] },
        includes: { location: ['state'] },
        sort: ['location_name asc'],
    }, !isEscalation && Boolean(filters.associate_company && filters.associate_company.value && filters.associate_company.value !== DEFAULT_VALUE));

    // ── Effects ───────────────────────────────────────────────────────────────

    // Dept-mapping effect: branches for escalation vs owner/manager roles.
    useEffect(() => {
        if (fetchingDeptUsers || !departmentUsers) return;

        if (isEscalation) {
            // Company-scoped response: classify users as owners/managers; collect verticals/depts.
            // Companies come from the user_companies hook (separate effect below).
            const _owners:      any[] = [];
            const _managers:    any[] = [];
            const _verticals:   any   = {};
            const _departments: any   = {};
            (departmentUsers as any[]).forEach(({ department, user }: any) => {
                const { vertical } = department;
                if (!_verticals[vertical.id])
                    _verticals[vertical.id] = { value: vertical.id, label: vertical.name, company_id: (vertical.company || {}).id };
                if (!_departments[department.id])
                    _departments[department.id] = { value: department.id, label: department.name, company_id: (vertical.company || {}).id, verticalId: vertical.id };
                const isOwnerUser   = (user.roles || []).some(({ pages }: any) => (pages || []).includes(USER_PRIVILEGES.OWNER_DASHBOARD));
                const isManagerUser = (user.roles || []).some(({ pages }: any) => (pages || []).includes(USER_PRIVILEGES.MANAGER_DASHBOARD));
                if (isOwnerUser)   _owners.push({   value: user.id, label: user.name });
                if (isManagerUser) _managers.push({ value: user.id, label: user.name });
            });
            setOwners(sortBy(uniq(_owners,   true, (u: any) => u.value), 'label'));
            setManagers(sortBy(uniq(_managers, true, (u: any) => u.value), 'label'));
            setVerticals(sortBy(Object.values(_verticals), 'label'));
            setDepartments(sortBy(Object.values(_departments), 'label'));
            return;
        }

        // ── Non-escalation (Owner / Manager) ────────────────────────────────────
        companiesLoadedRef.current = true;

        const _companies:   any    = {};
        const _verticals:   any    = {};
        const _departments: any    = {};
        const _owners:      any[]  = [];
        const _managers:    any[]  = [];
        setAssociateCompanies(undefined);
        setLocations(undefined);
        setStates([]);
        setAllLocations([]);

        departmentUsers.forEach(({ department, user_id, user }: any) => {
            const { vertical } = department;
            const { company }  = vertical;
            const { id, name } = company;
            if (!_companies[id] && user_id === loginUser) {
                _companies[id] = { value: id, label: name };
            }
            if (!_verticals[vertical.id] && _companies[id]) {
                _verticals[vertical.id] = { value: vertical.id, label: vertical.name, company_id: company.id };
            }
            if (!_departments[department.id] && _companies[id]) {
                _departments[department.id] = {
                    value: department.id, label: department.name,
                    company_id: company.id, verticalId: vertical.id,
                };
            }
            const isOwnerUser   = Boolean(user.roles.find(({ pages }: any) => pages.includes(USER_PRIVILEGES.OWNER_DASHBOARD)));
            const isManagerUser = Boolean(user.roles.find(({ pages }: any) => pages.includes(USER_PRIVILEGES.MANAGER_DASHBOARD)));
            if (isOwnerUser)   _owners.push({   value: user.id, label: user.name, company_id: company.id });
            if (isManagerUser) _managers.push({ value: user.id, label: user.name, company_id: company.id });
        });

        const comps: any[] = sortBy(Object.values(_companies), 'label');
        setCompanies(comps);
        // Skip auto-select when restoring saved state — the company was already
        // set in the mount restore effect above.
        if (comps.length > 0 && !restoredRef.current) {
            setFilters((prev: any) => ({ ...prev, company: comps[0] }));
        }

        // Deduplicate by user id and filter to companies the current user manages
        setOwners(sortBy(
            uniq(_owners.filter(({ company_id }: any) => Boolean(_companies[company_id])), true, (u: any) => u.value),
            'label',
        ));
        setManagers(sortBy(
            uniq(_managers.filter(({ company_id }: any) => Boolean(_companies[company_id])), true, (u: any) => u.value),
            'label',
        ));
        setVerticals(sortBy(Object.values(_verticals), 'label'));
        setDepartments(sortBy(Object.values(_departments), 'label'));
    }, [fetchingDeptUsers]);

    // ── Escalation: populate company list from user_companies tree ─────────────
    useEffect(() => {
        if (!isEscalation || fetchingUserCompanies || !userCompanies) return;
        companiesLoadedRef.current = true;
        const companyOptions = sortBy(
            (userCompanies as any[]).map((c: any) => ({ value: c.id, label: c.name })),
            'label',
        );
        setCompanies(companyOptions);
        // Auto-select first company, same as owner/manager behaviour.
        // Condition: no company yet OR still on the initial "All" placeholder.
        if (companyOptions.length > 0 && (!(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE)) {
            setFilters((prev: any) => ({ ...prev, company: companyOptions[0] }));
        }
    }, [fetchingUserCompanies, userCompanies]);

    // ── Escalation: derive ACs from nested tree when company changes ───────────
    useEffect(() => {
        if (!isEscalation || !(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE || !userCompanies) return;
        const comp = (userCompanies as any[]).find((c: any) => c.id === filters.company.value);
        const acOptions = sortBy(
            ((comp && comp.associate_companies) || []).map((ac: any) => ({ value: ac.id, label: ac.name })),
            'label',
        );
        setAssociateCompanies([DEFAULT_OPTION, ...acOptions]);
        setStates([]);
        setLocations(undefined);
        setAllLocations([]);
        // owners/managers re-fetch automatically — dept-mappings hook payload now carries the new company id
    }, [filters.company, userCompanies]);

    // ── Escalation: derive states + locations from nested tree when AC changes ──
    useEffect(() => {
        if (!isEscalation || !(filters.associate_company && filters.associate_company.value) || filters.associate_company.value === DEFAULT_VALUE || !userCompanies) return;
        const comp = (userCompanies as any[]).find((c: any) => c.id === (filters.company || {}).value);
        const ac   = ((comp && comp.associate_companies) || []).find((a: any) => a.id === filters.associate_company.value);
        const locs = (ac && ac.locations) || [];
        const _statesMap: any = {};
        const _locOpts: any[] = [];
        locs.forEach((loc: any) => {
            if (loc.state && !_statesMap[loc.state.id])
                _statesMap[loc.state.id] = { value: loc.state.id, label: loc.state.name };
            _locOpts.push({ value: loc.location_id || loc.id, label: loc.name, state_id: loc.state ? loc.state.id : null });
        });
        setStates([DEFAULT_OPTION, ...sortBy(Object.values(_statesMap), 'label')]);
        setAllLocations(_locOpts);
        setLocations([DEFAULT_OPTION, ..._locOpts]);
    }, [filters.associate_company, userCompanies]);

    // ── Non-escalation: ACs from separate API ────────────────────────────────
    useEffect(() => {
        if (isEscalation || fetchingAcs || !acs) return;
        setLocations(undefined);
        const sorted = sortBy(
            (acs || []).map(({ id, name }: any) => ({ value: id, label: name })),
            'label',
        );
        setAssociateCompanies([DEFAULT_OPTION, ...sorted]);
    }, [fetchingAcs]);

    // ── Non-escalation: locations from separate API ───────────────────────────
    useEffect(() => {
        if (isEscalation || fetchingLocs || !locs) return;
        const _states: any = {};
        const _locs: any[] = (locs || []).map(({ location }: any) => {
            if (location.state && !_states[location.state.id]) {
                _states[location.state.id] = { value: location.state.id, label: location.state.name };
            }
            return {
                value: location.id,
                label: location.name,
                state_id: location.state ? location.state.id : null,
            };
        });
        setAllLocations(_locs);
        setStates([DEFAULT_OPTION, ...sortBy(Object.values(_states), 'label')]);
        setLocations([DEFAULT_OPTION, ..._locs]);
    }, [fetchingLocs]);

    // Close more-filters on outside click
    useEffect(() => {
        function handler(e: MouseEvent) {
            if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
                setShowMore(false);
            }
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Auto-fire on first company load
    useEffect(() => {
        if (companiesLoadedRef.current) fireApply();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.company]);

    // Notify parent of every filter-state change so it can persist state to
    // sessionStorage before navigating to the listing page.
    useEffect(() => {
        if (onStateChange) {
            onStateChange({
                filters,
                timeRange,
                filterMonth,
                filterYear,
                monthYear,
                dueDate,
                selectedOwner,
                selectedManager,
                selectedVertical,
                selectedDepartment,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, timeRange, filterMonth, filterYear, monthYear, dueDate,
        selectedOwner, selectedManager, selectedVertical, selectedDepartment]);

    // ── Build payload ─────────────────────────────────────────────────────────
    // Accepts an explicit filter snapshot so change handlers can pass the new
    // values synchronously (React state updates are async, so filtersRef may lag).

    function buildPayloadFrom(
        snap: { company: any; associate_company: any; state: any; location: any },
        overrides?: Partial<ComplianceDashboardPayload>,
    ): ComplianceDashboardPayload {
        const { company, associate_company, state, location } = snap;
        const selectedTR = TIME_RANGE_OPTIONS.find((o) => o.value === timeRange) || DEFAULT_TIME_RANGE;
        const payload: ComplianceDashboardPayload = {};

        if (company && company.value && company.value !== DEFAULT_VALUE) {
            payload.company_id = [company.value];
        } else if (!isEscalation) {
            // Non-escalation: pre-scope to all companies assigned to this user.
            // Escalation: leave company_id absent so the escalationReady guard
            // in the dashboard blocks all API calls until a company is chosen.
            const ids = companies
                .filter((c: any) => c.value && c.value !== DEFAULT_VALUE)
                .map((c: any) => c.value);
            if (ids.length) payload.company_id = ids;
        }
        if (associate_company && associate_company.value && associate_company.value !== DEFAULT_VALUE)
            payload.associate_company_id = [associate_company.value];
        if (state && state.value && state.value !== DEFAULT_VALUE)
            payload.state_id = [state.value];
        if (location && location.value && location.value !== DEFAULT_VALUE)
            payload.location_id = [location.value];

        if (selectedTR.value === 'full') {
            // Full Year → tell the API to filter by the FY start year only
            payload.year_eq = parseInt(selectedTR.financial_year.split('-')[0], 10);
        } else {
            if (selectedTR.financial_year) payload.financial_year = selectedTR.financial_year;
            if (selectedTR.quarter)        payload.quarter = selectedTR.quarter;
        }
        if (monthYear)                 payload.month_year = monthYear;
        if (dueDate)                   payload.due_date = dueDate;
        if (selectedOwner      && selectedOwner.value)      payload.owner_id      = [selectedOwner.value];
        if (selectedManager    && selectedManager.value)    payload.manager_id    = [selectedManager.value];
        if (selectedVertical   && selectedVertical.value)   payload.vertical_id   = [selectedVertical.value];
        if (selectedDepartment && selectedDepartment.value) payload.department_id = [selectedDepartment.value];

        const merged = { ...payload, ...overrides };

        // A specific month_year supersedes the time-range entirely — never send both.
        // Drop financial_year / quarter / year_eq when month_year is present.
        if (merged.month_year) {
            delete merged.financial_year;
            delete merged.quarter;
            delete merged.year_eq;
        }

        return merged;
    }

    // Used by the company effect (filtersRef is already up-to-date by render time)
    function fireApply() { onApply(buildPayloadFrom(filtersRef.current)); }

    // ── Change handlers ───────────────────────────────────────────────────────

    function handleCompanyChange(opt: any) {
        const isAll = !opt || opt.value === DEFAULT_VALUE;
        setAssociateCompanies(undefined);
        setLocations(undefined);
        setStates([]);
        setAllLocations([]);
        // Reset More Filters so they don't carry over to the new company context
        setMonthYear(''); setFilterMonth(''); setFilterYear('');
        setDueDate('');
        setSelectedOwner(null);
        setSelectedManager(null);
        setSelectedVertical(null);
        setSelectedDepartment(null);
        setFilters((prev: any) => ({
            ...prev,
            company: opt || DEFAULT_OPTION,
            associate_company: isAll ? null : DEFAULT_OPTION,
            state: null,
            location: null,
        }));
        // company effect (useEffect on filters.company) fires fireApply() after render,
        // by which point all the reset state above will already be empty/null in the closure.
    }

    function handleACChange(opt: any) {
        const isAll = !opt || opt.value === DEFAULT_VALUE;
        setLocations(undefined);
        setStates([]);
        setAllLocations([]);
        const newFilters = {
            ...filtersRef.current,
            associate_company: opt || DEFAULT_OPTION,
            state: isAll ? null : DEFAULT_OPTION,
            location: isAll ? null : DEFAULT_OPTION,
        };
        setFilters(newFilters);
        onApply(buildPayloadFrom(newFilters));
    }

    function handleStateChange(opt: any) {
        const isAll = !opt || opt.value === DEFAULT_VALUE;
        const filteredLocs = isAll
            ? allLocations
            : allLocations.filter((l: any) => l.state_id === opt.value);
        setLocations([DEFAULT_OPTION, ...filteredLocs]);
        const newFilters = {
            ...filtersRef.current,
            state: opt || null,
            location: DEFAULT_OPTION,
        };
        setFilters(newFilters);
        onApply(buildPayloadFrom(newFilters));
    }

    function handleLocationChange(opt: any) {
        const newFilters = { ...filtersRef.current, location: opt || DEFAULT_OPTION };
        setFilters(newFilters);
        onApply(buildPayloadFrom(newFilters));
    }

    function handleTimeRangeChange(e: React.ChangeEvent<HTMLSelectElement>) {
        const val = e.target.value;
        setTimeRange(val);
        const selected = TIME_RANGE_OPTIONS.find((o) => o.value === val) || DEFAULT_TIME_RANGE;

        // setTimeRange is async — buildPayloadFrom still reads the OLD timeRange from
        // closure and would set the wrong time-range fields. Pass a complete override
        // that explicitly sets the new fields AND clears the ones that no longer apply,
        // so the stale-state output is fully neutralised by the { ...payload, ...overrides } spread.
        // (JSON.stringify drops undefined keys, so they are excluded from the request body.)
        const trOverride: Partial<ComplianceDashboardPayload> = selected.value === 'full'
            ? {
                year_eq:        parseInt(selected.financial_year.split('-')[0], 10),
                financial_year: undefined,
                quarter:        undefined,
              }
            : {
                year_eq:        undefined,
                financial_year: selected.financial_year || undefined,
                quarter:        selected.quarter        || undefined,
              };

        onApply(buildPayloadFrom(filtersRef.current, trOverride));
    }

    function handleMonthYearChange(e: React.ChangeEvent<HTMLInputElement>) {
        const val = e.target.value;
        setMonthYear(val);
        onApply(buildPayloadFrom(filtersRef.current, { month_year: val || undefined }));
    }

    function handleDueDateChange(e: React.ChangeEvent<HTMLInputElement>) {
        const val = e.target.value;
        setDueDate(val);
        onApply(buildPayloadFrom(filtersRef.current, { due_date: val || undefined }));
    }

    function handleOwnerChange(opt: any) {
        setSelectedOwner(opt);
        onApply(buildPayloadFrom(filtersRef.current, {
            owner_id: (opt && opt.value) ? [opt.value] : undefined,
        }));
    }

    function handleManagerChange(opt: any) {
        setSelectedManager(opt);
        onApply(buildPayloadFrom(filtersRef.current, {
            manager_id: (opt && opt.value) ? [opt.value] : undefined,
        }));
    }

    function handleVerticalChange(opt: any) {
        setSelectedVertical(opt && opt.value ? opt : null);
        onApply(buildPayloadFrom(filtersRef.current, {
            vertical_id: (opt && opt.value) ? [opt.value] : undefined,
        }));
    }

    function handleDepartmentChange(opt: any) {
        setSelectedDepartment(opt && opt.value ? opt : null);
        onApply(buildPayloadFrom(filtersRef.current, {
            department_id: (opt && opt.value) ? [opt.value] : undefined,
        }));
    }

    function handleMoreFiltersClear() {
        setMonthYear(''); setFilterMonth(''); setFilterYear('');
        setDueDate('');
        setSelectedOwner(null);
        setSelectedManager(null);
        setSelectedVertical(null);
        setSelectedDepartment(null);
        onApply(buildPayloadFrom(filtersRef.current, {
            month_year: undefined, due_date: undefined,
            owner_id: undefined, manager_id: undefined,
            vertical_id: undefined, department_id: undefined,
        }));
        setShowMore(false);
    }

    // ── Render ────────────────────────────────────────────────────────────────

    const labelStyle: React.CSSProperties = {
        fontSize: 12, color: '#757575', fontWeight: 500,
        fontFamily: 'Montserrat, sans-serif', marginBottom: 6, display: 'block',
    };

    return (
        <div className="cd2-filter-card">

            {/* ── Main filter row ── */}
            <div className="cd2-filter-row">

                {/* Company */}
                <div className="cd2-filter-group">
                    <label className="cd2-filter-label">Company</label>
                    <Select
                        styles={selectStyles}
                        menuPosition="fixed"
                        options={companies}
                        value={filters.company || DEFAULT_OPTION}
                        onChange={handleCompanyChange}
                        isLoading={isEscalation ? fetchingUserCompanies : fetchingDeptUsers}
                    />
                </div>

                {/* Associate Company */}
                <div className="cd2-filter-group">
                    <label className="cd2-filter-label">Associate Company</label>
                    <Select
                        styles={selectStyles}
                        menuPosition="fixed"
                        options={associateCompanies || [DEFAULT_OPTION]}
                        value={filters.associate_company || DEFAULT_OPTION}
                        onChange={handleACChange}
                        isDisabled={!associateCompanies}
                        isLoading={!isEscalation && fetchingAcs}
                    />
                </div>

                {/* State */}
                <div className="cd2-filter-group">
                    <label className="cd2-filter-label">State</label>
                    <Select
                        styles={selectStyles}
                        menuPosition="fixed"
                        options={states}
                        value={filters.state || DEFAULT_OPTION}
                        onChange={handleStateChange}
                        isDisabled={states.length === 0}
                    />
                </div>

                {/* Location */}
                <div className="cd2-filter-group">
                    <label className="cd2-filter-label">Location</label>
                    <Select
                        styles={selectStyles}
                        menuPosition="fixed"
                        options={locations || [DEFAULT_OPTION]}
                        value={filters.location || DEFAULT_OPTION}
                        onChange={handleLocationChange}
                        isDisabled={!locations}
                        isLoading={!isEscalation && fetchingLocs}
                    />
                </div>

                {/* Time Range */}
                <div className="cd2-filter-group">
                    <label className="cd2-filter-label">Time Range</label>
                    <select
                        className="cd2-time-select"
                        value={timeRange}
                        onChange={handleTimeRangeChange}
                    >
                        {TIME_RANGE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* More Filters — badge shows count of active filters */}
                <div style={{ position: 'relative', alignSelf: 'flex-end' }} ref={moreRef}>
                    {(() => {
                        const activeCount = [monthYear, dueDate, selectedOwner, selectedManager, selectedVertical, selectedDepartment].filter(Boolean).length;
                        return (
                            <button
                                className={'cd2-more-btn' + (activeCount > 0 ? ' cd2-more-btn-active' : '')}
                                onClick={() => setShowMore(!showMore)}
                            >
                                More Filters
                                {activeCount > 0 && (
                                    <span className="cd2-more-badge">{activeCount}</span>
                                )}
                            </button>
                        );
                    })()}

                    {showMore && (
                        <div style={{
                            position: 'absolute', top: '110%', right: 0, zIndex: 9999,
                            background: '#fff', border: '1px solid #e6e6e6', borderRadius: 8,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: 16, minWidth: 300,
                        }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 14 }}>
                                Additional Filters
                            </div>

                            {/* Vertical — auto-applies on change */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={labelStyle}>Vertical</label>
                                <Select
                                    menuPosition="fixed"
                                    options={[{ value: null, label: 'All' }, ...verticals.filter(({ company_id }: any) => {
                                        const company = filtersRef.current.company;
                                        return !company || company.value === DEFAULT_VALUE ? true : company_id === company.value;
                                    })]}
                                    value={selectedVertical || { value: null, label: 'All' }}
                                    onChange={handleVerticalChange}
                                    isClearable={false}
                                    styles={{
                                        control: (b: any) => ({ ...b, minHeight: 34, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }),
                                        menu:    (b: any) => ({ ...b, fontSize: 12, fontFamily: 'Montserrat, sans-serif', zIndex: 10000 }),
                                        option:  (b: any, s: any) => ({ ...b, fontSize: 12, backgroundColor: s.isSelected ? '#2965ad' : s.isFocused ? '#f0f5ff' : '#fff', color: s.isSelected ? '#fff' : '#1e1e1e' }),
                                    }}
                                />
                            </div>

                            {/* Department — auto-applies on change */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={labelStyle}>Department</label>
                                <Select
                                    menuPosition="fixed"
                                    options={[{ value: null, label: 'All' }, ...departments.filter(({ company_id, verticalId }: any) => {
                                        const company = filtersRef.current.company;
                                        if (selectedVertical && selectedVertical.value) return verticalId === selectedVertical.value;
                                        if (company && company.value && company.value !== DEFAULT_VALUE) return company_id === company.value;
                                        return true;
                                    })]}
                                    value={selectedDepartment || { value: null, label: 'All' }}
                                    onChange={handleDepartmentChange}
                                    isClearable={false}
                                    styles={{
                                        control: (b: any) => ({ ...b, minHeight: 34, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }),
                                        menu:    (b: any) => ({ ...b, fontSize: 12, fontFamily: 'Montserrat, sans-serif', zIndex: 10000 }),
                                        option:  (b: any, s: any) => ({ ...b, fontSize: 12, backgroundColor: s.isSelected ? '#2965ad' : s.isFocused ? '#f0f5ff' : '#fff', color: s.isSelected ? '#fff' : '#1e1e1e' }),
                                    }}
                                />
                            </div>

                            {/* Owner — visible for manager login OR escalation */}
                            {(isManager || isEscalation) && (
                                <div style={{ marginBottom: 14 }}>
                                    <label style={labelStyle}>Owner</label>
                                    <Select
                                        menuPosition="fixed"
                                        options={[{ value: null, label: 'All' }, ...owners]}
                                        value={selectedOwner || { value: null, label: 'All' }}
                                        onChange={handleOwnerChange}
                                        isClearable={false}
                                        isDisabled={isEscalation && (!(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE)}
                                        placeholder={isEscalation && (!(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE) ? 'Select company first' : 'All'}
                                        styles={{
                                            control: (b: any) => ({ ...b, minHeight: 34, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }),
                                            menu:    (b: any) => ({ ...b, fontSize: 12, fontFamily: 'Montserrat, sans-serif', zIndex: 10000 }),
                                            option:  (b: any, s: any) => ({ ...b, fontSize: 12, backgroundColor: s.isSelected ? '#2965ad' : s.isFocused ? '#f0f5ff' : '#fff', color: s.isSelected ? '#fff' : '#1e1e1e' }),
                                        }}
                                    />
                                </div>
                            )}

                            {/* Manager — visible for owner login OR escalation */}
                            {(isOwner || isEscalation) && (
                                <div style={{ marginBottom: 14 }}>
                                    <label style={labelStyle}>Manager</label>
                                    <Select
                                        menuPosition="fixed"
                                        options={[{ value: null, label: 'All' }, ...managers]}
                                        value={selectedManager || { value: null, label: 'All' }}
                                        onChange={handleManagerChange}
                                        isClearable={false}
                                        isDisabled={isEscalation && (!(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE)}
                                        placeholder={isEscalation && (!(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE) ? 'Select company first' : 'All'}
                                        styles={{
                                            control: (b: any) => ({ ...b, minHeight: 34, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }),
                                            menu:    (b: any) => ({ ...b, fontSize: 12, fontFamily: 'Montserrat, sans-serif', zIndex: 10000 }),
                                            option:  (b: any, s: any) => ({ ...b, fontSize: 12, backgroundColor: s.isSelected ? '#2965ad' : s.isFocused ? '#f0f5ff' : '#fff', color: s.isSelected ? '#fff' : '#1e1e1e' }),
                                        }}
                                    />
                                </div>
                            )}

                            {/* Month & Year — two <select> dropdowns for cross-browser compat
                                (<input type="month"> is unsupported in Firefox / older browsers) */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={labelStyle}>Month &amp; Year</label>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <select
                                        value={filterMonth}
                                        onChange={(e) => {
                                            const m = e.target.value;
                                            setFilterMonth(m);
                                            const combined = (filterYear && m) ? filterYear + '-' + m : '';
                                            setMonthYear(combined);
                                            onApply(buildPayloadFrom(filtersRef.current, { month_year: combined || undefined }));
                                        }}
                                        style={{ ...dateInputStyle, flex: 1 }}
                                    >
                                        <option value="">Month</option>
                                        {MONTH_OPTIONS.map((mo) => (
                                            <option key={mo.value} value={mo.value}>{mo.label}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={filterYear}
                                        onChange={(e) => {
                                            const y = e.target.value;
                                            setFilterYear(y);
                                            const combined = (y && filterMonth) ? y + '-' + filterMonth : '';
                                            setMonthYear(combined);
                                            onApply(buildPayloadFrom(filtersRef.current, { month_year: combined || undefined }));
                                        }}
                                        style={{ ...dateInputStyle, flex: 1 }}
                                    >
                                        <option value="">Year</option>
                                        {buildMonthYearOptions().map((y) => (
                                            <option key={y} value={String(y)}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Due Date — auto-applies on change */}
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Due Date</label>
                                <input
                                    type="date"
                                    value={dueDate}
                                    onChange={handleDueDateChange}
                                    style={dateInputStyle}
                                />
                            </div>

                            {/* Clear only — no Apply button needed */}
                            <button
                                style={{
                                    width: '100%', height: 38, background: '#fff', color: '#6b7280',
                                    border: '1px solid #e6e6e6', borderRadius: 8, fontSize: 13,
                                    fontWeight: 600, cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
                                }}
                                onClick={handleMoreFiltersClear}
                            >
                                Clear Filters
                            </button>
                        </div>
                    )}
                </div>
            </div>

        {/* ── Activity Type Tabs ── */}
        <div className="cd2-category-tabs">
            {ACTIVITY_TABS.map(function(tab) {
                return (
                    <button
                        key={tab}
                        className={'cd2-category-tab' + (selectedTab === tab ? ' active' : '')}
                        onClick={function() { onTabChange(tab); }}
                    >
                        {tab}
                    </button>
                );
            })}
        </div>

        </div>
    );
}
